import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D11fromD1 = () => {
  const title = "Calculation of D11 (Ekadashamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D11 chart, or Ekadashamsa chart, is used in Vedic astrology to
        analyze gains, accomplishments, social recognition, and honor. Below is
        the step-by-step process to calculate the D11 chart from the D1 (Rasi)
        chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D11 (Ekadashamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Mercury is located at 17° Leo, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Eleven Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into eleven equal parts, each spanning
        approximately 2.7272°. To find the Ekadashamsa position of a planet, we
        divide its degree in the sign by 2.7272.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Ekadashamsa \ Position = \frac{Degree \ in \ Sign}{2.7272}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{2.7272} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{2.7272} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{2.7272} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`3 \leq \frac{Degree \ in \ Sign}{2.7272} < 4`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fourth 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`4 \leq \frac{Degree \ in \ Sign}{2.7272} < 5`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fifth 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`5 \leq \frac{Degree \ in \ Sign}{2.7272} < 6`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>sixth 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`6 \leq \frac{Degree \ in \ Sign}{2.7272} < 7`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>seventh 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`7 \leq \frac{Degree \ in \ Sign}{2.7272} < 8`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>eighth 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`8 \leq \frac{Degree \ in \ Sign}{2.7272} < 9`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>ninth 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`9 \leq \frac{Degree \ in \ Sign}{2.7272} < 10`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>tenth 2.7272°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`10 \leq \frac{Degree \ in \ Sign}{2.7272} < 11`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>eleventh 2.7272°</strong>.
        </Typography>
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Assign the Ekadashamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>11th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Ninth 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Tenth 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eleventh 2.7272°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Mercury at 17° Leo
      </Typography>

      <Typography variant="body1" paragraph>
        Leo is the D1 sign. Mercury at 17° falls in the seventh 2.7272° (between
        16.3632° and 19.0904°). Therefore, it belongs to the{" "}
        <strong>10th sign</strong> from Leo, which is <strong>Taurus</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D11 (Ekadashamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D11
        (Ekadashamsa) position from the D1 (Rasi) chart, using the planet's
        degree and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_ekadashamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Ekadashamsa the planet is in based on its degree
    if degree <= 2.7272:
        # First 2.7272° belongs to the same sign
        ekadashamsa_sign = zodiac_signs[sign_index]
    elif 2.7272 < degree <= 5.4544:
        # Second 2.7272° belongs to the 11th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 5.4544 < degree <= 8.1816:
        # Third 2.7272° belongs to the 7th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 8.1816 < degree <= 10.9088:
        # Fourth 2.7272° belongs to the 6th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 10.9088 < degree <= 13.6360:
        # Fifth 2.7272° belongs to the 5th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 13.6360 < degree <= 16.3632:
        # Sixth 2.7272° belongs to the 4th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 16.3632 < degree <= 19.0904:
        # Seventh 2.7272° belongs to the 10th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 19.0904 < degree <= 21.8176:
        # Eighth 2.7272° belongs to the 3rd sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 21.8176 < degree <= 24.5448:
        # Ninth 2.7272° belongs to the 9th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 24.5448 < degree <= 27.2720:
        # Tenth 2.7272° belongs to the 8th sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    else:
        # Eleventh 2.7272° belongs to the 2nd sign from the current sign
        ekadashamsa_sign = zodiac_signs[(sign_index + 1) % 12]

    return ekadashamsa_sign

# Example usage
planet = "Mercury"
degree = 17
sign = "Leo"

ekadashamsa_sign = calculate_ekadashamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {ekadashamsa_sign} in the D11 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Ekadashamsa for a planet based
        on its degree and sign in the D1 chart. The program divides the sign
        into eleven equal parts and assigns the Ekadashamsa based on the
        planet's degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into eleven parts: the first 2.7272°, the
        second 2.7272°, and so on. It assigns the Ekadashamsa based on the
        planet's degree within the sign:
        <br />
        - First 2.7272° belongs to the same sign.
        <br />
        - Second 2.7272° belongs to the 11th sign from the D1 sign.
        <br />
        - Third 2.7272° belongs to the 7th sign from the D1 sign.
        <br />
        - Fourth 2.7272° belongs to the 6th sign from the D1 sign.
        <br />
        - Fifth 2.7272° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 2.7272° belongs to the 4th sign from the D1 sign.
        <br />
        - Seventh 2.7272° belongs to the 10th sign from the D1 sign.
        <br />
        - Eighth 2.7272° belongs to the 3rd sign from the D1 sign.
        <br />
        - Ninth 2.7272° belongs to the 9th sign from the D1 sign.
        <br />
        - Tenth 2.7272° belongs to the 8th sign from the D1 sign.
        <br />- Eleventh 2.7272° belongs to the 2nd sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D11fromD1;
